import React, { useCallback, useMemo, useState } from 'react';
import Datagrid from '../../datagrid/index.jsx';
import { Button } from '../../bootstrap/index.jsx';
import IndicatorToolbar from './IndicatorToolbar.jsx';
import DropdownMenu from '../../common/DropdownMenu.jsx';
import Select from '../../common/Select.jsx';
import { createDefaultDatagridExportOptions } from '../../common/AgGrid/ExportFilename.jsx';

import { datagrid_get_path } from '~/routes';

const INPUT_GRID_HEIGHT = 450;
const GRID_DUMMY_STYLE = {
  display: 'flex', 
  justifyContent: 'center', 
  alignItems: 'center', 
  height: INPUT_GRID_HEIGHT,
  flexDirection: 'column',
  border: '1px solid #CCC',
  background: '#f1f1f1'
}

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: 240,
    margin: '1rem',
    border: '1px solid #bbb'
  }),
  menu: () => ({}), // Empty to clear default absolute styling
  container: (provided, state) => ({
    ...provided,
    border: 'none',
  })
};

const InputDatagrid = ({datagrid, dispatch, formOptions, filterModel = {}, projectId = null, canResetDatagrid = false, withSideBar = true}) => {
  const [advancedFilterModel, setAdvancedFilterModel] = useState(filterModel);

  const sideBar = useMemo(() => {
    return {
      toolPanels: [  
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
      defaultToolPanel: 'filters',
    };
  }, []);

  // Applying a temporary solution for parsing and formatting filter values ahead of processing.
  const processFilters = (data, columnDefs, isFormatting) => {
    if (_.isEmpty(data)) return {};

    const processCondition = (cond) => {
      let col = columnDefs.find(c => c.field === cond.colId);
      const valueProcessor = isFormatting ? col.valueFormatter : col.valueParser;
      return { ...cond, filter: isFormatting && !col.cellEditorParams?.values ? cond.filter : valueProcessor?.({[isFormatting ? 'value' : 'newValue']: cond.filter, colDef: { cellRendererParams: col.cellRendererParams }}) || cond.filter};
    };

    if (data.conditions) {
      // If the structure has conditions, loop through each of them
       return {...data, conditions: data.conditions.map(processCondition)};
    } else {
      if (data.filter) {
        // If it has a filter attribute, log it
        let col = columnDefs.find(c => c.field === data.colId);
        const valueProcessor = isFormatting ? col.valueFormatter : col.valueParser;
        data.filter = isFormatting && !col.cellEditorParams?.values ? data.filter : valueProcessor?.({[isFormatting ? 'value' : 'newValue']: data.filter, colDef: { cellRendererParams: col.cellRendererParams }}) || data.filter;
      }
      return { ...data };
    }
  };

  /**
   * Recursively retrieves all columns from an ag-Grid column definition, including those that are organized under group headers
   *
   * @returns {Array} An array of all column objects without any nested structure, combining all the columns from the input
   */
  const getAllColumns = (columns) => {
    let allColumns = [];
    columns.forEach(col => {
      if (col.children) {
        allColumns = [...allColumns, ...getAllColumns(col.children)]; // Recursively get children
      } else {
        allColumns.push(col); // Push the column if it has no children
      }
    });
    return allColumns;
  };

  const onFilterChanged = useCallback((event) => {
    if (!datagrid.id) return;

    let filterModel = processFilters(event.api.getAdvancedFilterModel(), getAllColumns(event.api.getColumnDefs()), false); // For parsing
    setAdvancedFilterModel(filterModel);

    dispatch({type: 'set_input_filters', filterModel: filterModel, datagrid_id: datagrid.id})
  }, [dispatch, datagrid]);

  const onResetDatagrid = (e) => {
    e.preventDefault();
    if (confirm(I18n.t('confirm_reset_datagrid')))
      dispatch({type: 'reset_datagrid'});
  };

  const onGridReady = (params) => {
    params.api.setGridOption('advancedFilterParent', document.getElementById('input-datagrid-filter-container'));
    if (!_.isEmpty(advancedFilterModel)) {
      let filterModel = processFilters(advancedFilterModel, getAllColumns(params.api.getColumnDefs()), true); // For formatting
      params.api.setAdvancedFilterModel(filterModel);
    }
  };

  const datagridSelect = useMemo(() => (
    <div style={{padding: '1rem'}}>
      <Select 
        options={formOptions.map(o => ({label: o.name, value: o.id}))}
        onChange={(value) => { dispatch({type: 'add_input', datagrid_id: value, input_def: {}})}}
        autoFocus
        menuIsOpen
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={false}
        backspaceRemovesValue={false}
        styles={selectStyles} // Intentionally override Select.jsx styles
        placeholder={I18n.t('search') + '...'}
      />
    </div>
  ), [formOptions, dispatch]);

  if (!datagrid) {
    return <div style={GRID_DUMMY_STYLE}>
      <p style={{maxWidth: 400, opacity: 0.8}}>{I18n.t('indicator_select_grid')}</p>
      <DropdownMenu menuItems={datagridSelect}>
        <Button size='sm' type='default'>{I18n.t('select')}</Button>
      </DropdownMenu>

    </div>
  }

  return <>
    {canResetDatagrid &&
      <a href="#" onClick={onResetDatagrid}>{I18n.t('reset_datagrid')}</a>
    }
    <h4 className='font-weight-normal'>{datagrid.name}</h4>

    <Datagrid
      datagrid={datagrid}
      context={{
        id: datagrid.id, 
        permissions: {}, 
        context_name: 'datagrid',
        query_path: datagrid_get_path({id: datagrid.id, project_id: projectId, filterModel: advancedFilterModel, _options: true})
      }}
      wrapperStyle={{maxHeight: INPUT_GRID_HEIGHT}}
      CustomToolbar={IndicatorToolbar}
      supressBulkActions={true}
      agGridProps={{
        sideBar: withSideBar ? sideBar : {},
        isRowSelectable: false,
        onFilterChanged: onFilterChanged,
        enableAdvancedFilter: true,
        gridReady: onGridReady
      }}
      gridOptions={createDefaultDatagridExportOptions(datagrid.name)}
    />
  </>
};

export default InputDatagrid;